import { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, CircularProgress, Alert, Chip, InputAdornment, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Inventory as AssetIcon, Search as SearchIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const blank = {
  a_code: '', name: '', type: '', dat: new Date().toISOString().slice(0, 10),
  cost: 0, dep_val: 0, year_dep: 0, charge: 0, r_name: '', qut: 1, uint: 0, location: '',
};

export default function AssetsPage() {
  const [rows, setRows]  = useState([]);
  const [loading, setL]  = useState(true);
  const [q, setQ]        = useState('');
  const [type, setType]  = useState('');

  const [open, setOpen]  = useState(false);
  const [editing, setEd] = useState(null);
  const [form, setForm]  = useState(blank);
  const [saving, setSav] = useState(false);
  const [error, setErr]  = useState('');

  const load = async () => {
    setL(true);
    try {
      const params = {};
      if (q)    params.q    = q;
      if (type) params.type = type;
      setRows((await API.get('/assets', { params })).data);
    } catch (e) { setErr(e.response?.data?.message || 'Load failed'); }
    finally { setL(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, type]);

  const openNew  = () => { setEd(null); setForm(blank); setErr(''); setOpen(true); };
  const openEdit = (r) => { setEd(r); setForm({ ...blank, ...r }); setErr(''); setOpen(true); };

  const submit = async () => {
    if (!form.a_code || !form.name) { setErr('Code and name are required'); return; }
    setSav(true); setErr('');
    try {
      if (editing) {
        await API.put(`/assets/${editing.a_code}`, form);
        setOpen(false);
        swal.success('Asset updated');
      } else {
        await API.post('/assets', form);
        const name = form.name;
        setForm((f) => ({ ...blank, dat: f.dat, type: f.type, location: f.location }));
        swal.success(`Asset "${name}" added — add another or close`);
      }
      await load();
    } catch (e) { setErr(swal.pickError(e, 'Save failed')); }
    finally { setSav(false); }
  };

  const remove = async (r) => {
    const ok = await swal.confirmDelete(`asset "${r.name}"`);
    if (!ok) return;
    try {
      await API.delete(`/assets/${r.a_code}`);
      swal.success('Asset deleted');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const types = Array.from(new Set(rows.map(r => r.type).filter(t => t && t !== '-')));
  const totalCost = rows.reduce((s, r) => s + Number(r.cost || 0), 0);

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <ModuleHeader icon={<AssetIcon sx={{ fontSize: 28 }} />}
        title="Assets" subtitle="Fixed asset register and depreciation"
        actionLabel="New Asset" actionIcon={<AddIcon />} onAction={openNew} />

      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search name or code…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">All</MenuItem>
            {types.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

      <Card>
        {loading ? <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table>
              <TableHead><TableRow>
                <TableCell>Code</TableCell><TableCell>Name</TableCell>
                <TableCell>Type</TableCell><TableCell>Location</TableCell>
                <TableCell>Acquired</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.a_code} hover>
                    <TableCell><Chip size="small" label={r.a_code} sx={{ fontFamily: 'monospace' }} /></TableCell>
                    <TableCell><strong>{r.name}</strong></TableCell>
                    <TableCell>{r.type && r.type !== '-' ? r.type : '—'}</TableCell>
                    <TableCell>{r.location && r.location !== '-' ? r.location : '—'}</TableCell>
                    <TableCell>{r.dat || '—'}</TableCell>
                    <TableCell align="right">{r.qut || 0}</TableCell>
                    <TableCell align="right"><strong>{Number(r.cost || 0).toFixed(2)}</strong></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.disabled' }}>No assets</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {rows.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">{rows.length} asset(s)</Typography>
            <Typography variant="body2" fontWeight={700}>Total Cost: {totalCost.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</Typography>
          </Box>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff',
        }}>
          {editing ? `Edit · ${editing.name}` : 'New Asset'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Asset Code" value={form.a_code} onChange={(e) => setForm({ ...form, a_code: e.target.value })} required disabled={!!editing} />
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="e.g. Furniture" />
            <TextField label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <TextField label="Acquisition Date" type="date" InputLabelProps={{ shrink: true }} value={form.dat} onChange={(e) => setForm({ ...form, dat: e.target.value })} />
            <TextField label="Responsible Person" value={form.r_name} onChange={(e) => setForm({ ...form, r_name: e.target.value })} />
            <TextField label="Quantity" type="number" value={form.qut} onChange={(e) => setForm({ ...form, qut: e.target.value })} />
            <TextField label="Unit Cost" type="number" value={form.uint} onChange={(e) => setForm({ ...form, uint: e.target.value })} />
            <TextField label="Total Cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            <TextField label="Annual Depreciation" type="number" value={form.dep_val} onChange={(e) => setForm({ ...form, dep_val: e.target.value })} />
            <TextField label="Useful Life (years)" type="number" value={form.year_dep} onChange={(e) => setForm({ ...form, year_dep: e.target.value })} />
            <TextField label="Depreciation Charge" type="number" value={form.charge} onChange={(e) => setForm({ ...form, charge: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {editing ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
